/**
 * Batches Table
 *
 * @see sprint-datagrid-migration Phase 3.2
 */

import { useMemo, useCallback } from 'react';
import { FileBox } from 'lucide-react';
import {
  DataGrid,
  EmptyState,
  textColumn,
  moneyColumn,
  integerColumn,
  dateTimeColumn,
  type DataGridColumn,
  type DataGridSortState,
} from '@bill/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import type { SalesImportBatchListItem, ImportBatchStatus, ImportSource, PaginationInfo } from '@bstock/shared';

export type SortField = 'createdAt' | 'startDate' | 'totalAmount' | 'totalReceipts';
export type SortOrder = 'asc' | 'desc';

const SORT_FIELD_MAP: Record<string, SortField> = {
  startDate: 'startDate',
  totalReceipts: 'totalReceipts',
  totalAmount: 'totalAmount',
  createdAt: 'createdAt',
};

interface BatchesTableProps {
  batches: SalesImportBatchListItem[];
  pagination: PaginationInfo;
  isLoading?: boolean;
  onRowClick: (batchId: string) => void;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  sortBy?: SortField;
  sortOrder?: SortOrder;
  onSort?: (field: SortField, order: SortOrder) => void;
  showSiteColumn?: boolean;
}

function StatusBadge({ status }: { status: ImportBatchStatus }) {
  const colors: Record<ImportBatchStatus, string> = {
    COMPLETED: 'bg-green-100 text-green-700',
    PENDING: 'bg-yellow-100 text-yellow-700',
    PROCESSING: 'bg-blue-100 text-blue-700',
    FAILED: 'bg-red-100 text-red-700',
    PARTIAL: 'bg-orange-100 text-orange-700',
  };
  const labels: Record<ImportBatchStatus, string> = {
    COMPLETED: 'Complet',
    PENDING: 'En attente',
    PROCESSING: 'En cours',
    FAILED: 'Echec',
    PARTIAL: 'Partiel',
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status]}`}>
      {labels[status]}
    </span>
  );
}

function SourceBadge({ source }: { source: ImportSource }) {
  const colors: Record<ImportSource, string> = {
    API_BILL: 'bg-blue-100 text-blue-700',
    MANUAL: 'bg-gray-100 text-gray-700',
    SEED: 'bg-purple-100 text-purple-700',
    MANUAL_RESYNC: 'bg-amber-100 text-amber-700',
    GAP_SWEEP: 'bg-teal-100 text-teal-700',
  };
  const labels: Record<ImportSource, string> = {
    API_BILL: 'API_BILL',
    MANUAL: 'MANUEL',
    SEED: 'SEED',
    MANUAL_RESYNC: 'RE-SYNCHRO',
    GAP_SWEEP: 'RATTRAPAGE',
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[source]}`}>
      {labels[source]}
    </span>
  );
}

export function BatchesTable({
  batches,
  pagination,
  isLoading,
  onRowClick,
  onPageChange,
  onLimitChange,
  sortBy = 'createdAt',
  sortOrder = 'desc',
  onSort,
  showSiteColumn = false,
}: BatchesTableProps) {
  const currency = useCurrency();
  const { formatDateTimeShort, formatDateTime } = useFormatDate();

  const formatPrecise = (date: string) => formatDateTime(date, { format: 'dd/MM/yyyy HH:mm:ss.SSS' });

  const columns = useMemo<DataGridColumn<SalesImportBatchListItem>[]>(() => {
    const cols: DataGridColumn<SalesImportBatchListItem>[] = [];

    if (showSiteColumn) {
      cols.push(
        textColumn<SalesImportBatchListItem>({
          id: 'brandName',
          header: 'Site',
          accessor: 'brandName',
          sortable: false,
        }),
      );
    }

    cols.push(
      textColumn<SalesImportBatchListItem>({
        id: 'startDate',
        header: 'Periode',
        sortable: true,
        accessor: (r) => r.startDate,
        exportValue: (r) => `${formatPrecise(r.startDate)} → ${formatPrecise(r.endDate)}`,
        cell: (r) => (
          <div>
            <div className="text-sm text-gray-900 font-mono">Du {formatPrecise(r.startDate)}</div>
            <div className="text-sm text-gray-900 font-mono">Au {formatPrecise(r.endDate)}</div>
          </div>
        ),
      }),
      textColumn<SalesImportBatchListItem>({
        id: 'source',
        header: 'Source',
        sortable: false,
        accessor: 'source',
        cell: (r) => <SourceBadge source={r.source} />,
      }),
      textColumn<SalesImportBatchListItem>({
        id: 'status',
        header: 'Statut',
        align: 'center',
        sortable: false,
        exportValue: (r) => r.status,
        cell: (r) => <StatusBadge status={r.status} />,
      }),
      integerColumn<SalesImportBatchListItem>({
        id: 'totalReceipts',
        header: 'Tickets',
        accessor: 'totalReceipts',
        align: 'center',
      }),
      moneyColumn<SalesImportBatchListItem>({
        id: 'totalAmount',
        header: 'Montant',
        accessor: (r) => r.totalAmount / 100,
      }),
      dateTimeColumn<SalesImportBatchListItem>({
        id: 'createdAt',
        header: 'Cree le',
        accessor: (r) => new Date(r.createdAt),
      }),
      textColumn<SalesImportBatchListItem>({
        id: 'completedAt',
        header: 'Termine le',
        sortable: false,
        accessor: (r) => (r.completedAt ? r.completedAt : null),
        cell: (r) => (
          <span className="text-sm text-gray-500">
            {r.completedAt ? formatDateTimeShort(r.completedAt) : '—'}
          </span>
        ),
      }),
    );

    return cols;
  }, [showSiteColumn, currency, formatPrecise, formatDateTimeShort]);

  // ---- Sort (server-side, controlled) ------------------------------------
  const sortState = useMemo<DataGridSortState | null>(() => {
    if (!sortBy || !sortOrder) return null;
    const columnId = Object.entries(SORT_FIELD_MAP).find(([, v]) => v === sortBy)?.[0];
    if (!columnId) return null;
    return { columnId, direction: sortOrder };
  }, [sortBy, sortOrder]);

  const handleSortChange = useCallback(
    (next: DataGridSortState | null) => {
      if (!onSort) return;
      if (!next) {
        onSort('createdAt', 'desc');
        return;
      }
      const backendField = SORT_FIELD_MAP[next.columnId];
      if (backendField) onSort(backendField, next.direction);
    },
    [onSort],
  );

  if (!isLoading && batches.length === 0) {
    return (
      <EmptyState
        icon={FileBox}
        title="Aucun batch trouve"
        description="Aucun batch d'import ne correspond a vos criteres de recherche. Essayez de modifier vos filtres."
      />
    );
  }

  return (
    <DataGrid<SalesImportBatchListItem>
      data={batches}
      columns={columns}
      rowKey={(r) => r.id}
      isLoading={isLoading}
      skeletonRows={8}
      onRowClick={(r) => onRowClick(r.id)}
      currency={currency}
      sortState={sortState}
      onSortChange={handleSortChange}
      exportable={{
        fileName: { base: 'imports_ventes' },
        sheetName: 'Imports Ventes',
        position: 'header',
      }}
      pagination={{
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        totalPages: pagination.totalPages,
        onPageChange,
        onLimitChange,
      }}
      emptyState={
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucun batch trouve
        </div>
      }
    />
  );
}
